import React, {useEffect, useState} from 'react';
import {Button, Checkbox, Label, Spinner, TextInput} from "flowbite-react";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {toast} from "react-toastify";
import { registerWithEmailAndPassword } from "../../services/FirebaseService";
import {getUserByUID} from "../../services/userService";

interface SignupData {
    email: string;
    password: string;
    repeatPassword: string;
    agree: any | true | false;
}

const SignupForm: React.FC = () => {
    const navigate = useNavigate();
    let [searchParams] = useSearchParams();
    const [loading, setLoading] = useState<boolean>(true);
    const [loader, setLoader] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [isDisable, setIsDisable] = useState(false);
    const [signupError, setSignupError] = useState('');
    const [form, setForm] = useState<SignupData>({
        email: "",
        password: "",
        repeatPassword: "",
        agree: isChecked
    });

    useEffect(() => {
        if (searchParams.get('UID')) {
            getUserByUId(searchParams.get('UID'));
        }
    }, [searchParams.get('UID')]);

    const getUserByUId = async (userId: string | any) => {
        setLoading(true)
        const userData = await getUserByUID(userId);
        setForm({...form, email: userData.email})
        setLoading(false)
        return userData;
    }

    const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsChecked(!isChecked);
        const { id, value } = e.target;
        setForm({ ...form, [id]: value })
    }

    const submitForm = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        setLoader(true);
        const {email, password, repeatPassword, agree} = form;
        if((email && password && repeatPassword && agree) && (password === repeatPassword)){
            try {
                const res = await registerWithEmailAndPassword(form);
                if (res && res?.user && res.user?.uid) {
                    setIsDisable(isDisable)
                    toast.success("Signup successfully");
                    navigate('/');
                } else {
                    setSignupError('User already registered.')
                }
                setLoader(false);
            } catch (e: any) {
                toast.warn(e.message);
                setLoader(false);
            }

        } else if ((email && password && repeatPassword && agree) && (password !== repeatPassword)) {
            toast.warn("Password did not match")
        }
    }

    return (
        <div className="md:container md:mx-auto h-[91vh]">
            {loading ?
                <div className="h-full flex justify-center items-center">
                    <div role="status" className="">
                        <svg
                            className="inline mr-2 w-16 h-16 text-gray-200 animate-spin dark:text-gray-600 fill-gray-900 !stroke-[4]"
                            viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor"/>
                            <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill"/>
                        </svg>
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
                :
                <div className="flex justify-center items-center text-center h-full">
                    <form className="flex flex-col gap-4 w-full lg:w-[30rem] lg:p-0 px-[5rem] lg:pt-3 md:pt-8 py-8"
                          onSubmit={submitForm}>
                        <h3 className="text-2xl mb-2 font-bold"> Create Your Account </h3>
                        <hr className="mb-5"/>
                        <div>
                            <TextInput
                                id="email"
                                type="email"
                                value={form.email}
                                placeholder="name@example.com"
                                required={true}
                                shadow={true}
                                onChange={changeHandler}
                                readOnly
                            />
                        </div>
                        <div>
                            <TextInput
                                id="password"
                                type="password"
                                value={form.password}
                                placeholder="Password"
                                required={true}
                                shadow={true}
                                onChange={changeHandler}
                            />
                        </div>
                        <div>
                            <TextInput
                                id="repeatPassword"
                                type="password"
                                value={form.repeatPassword}
                                placeholder="Repeat password"
                                required={true}
                                shadow={true}
                                onChange={changeHandler}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            {form?.email ?
                                <Checkbox id="agree" required={true} value={form.agree} onChange={changeHandler}/>
                                :
                                <Checkbox id="agree" required={true} value={form.agree} onChange={changeHandler}
                                          disabled/>
                            }
                            <Label htmlFor="agree">
                                I agree with the{' '}
                                <a
                                    href="/forms"
                                    className="hover:underline text-[#7325A3]"
                                >
                                    terms and conditions
                                </a>
                            </Label>
                        </div>

                        {
                            form?.email ?
                                <Button color="dark" size="lg" type="submit">
                                    {
                                        loader ? <Spinner aria-label="Default status example"/> : "Signup"
                                    }
                                </Button> :
                                <>
                                    <Button color="dark" size="lg" type="submit" disabled>
                                        Signup
                                    </Button>
                                    <p className="text-red-700 text-lg">Invalid or No token</p>
                                </>
                        }
                        {signupError && <p className="text-red-700 text-md text-left">{signupError}</p>}

                        <div>
                        <span className="text-xs">By signing up, you agree to our <span
                            className="underline hover:text-[#7325A3]"><a
                            href="/">Terms of Use</a></span> and <span className="underline hover:text-[#7325A3]"><a
                            href="/">Privacy Policy</a></span>.</span>
                        </div>
                        <span className="w-full bg-zinc-300 my-2 h-px"></span>
                        <div>
                            <span className="font-normal text-sm"> Already have an account?</span>
                            <span className="text-base text-[#7325A3] font-bold underline ml-1">
                        <Link to="/login">Log in</Link>
                    </span>
                        </div>
                    </form>
                </div>
            }
        </div>
    );
};

export default SignupForm;
